<template>
  <main class="development-evaluation-page">
    <CompanyBreadcrumb :aria-label="$t('general.development_evaluation')"
                       :home-aria-label="$t('general.home')"
                       :items="breadcrumbItems"
    />

    <div class="dev-eval-header">
      <div class="dev-eval-header-left">
        <h2 class="dev-eval-title">
          <span>{{ $t('general.development_evaluation') }}</span>
        </h2>
        <p class="dev-eval-description">
          {{ $t('general.development_evaluation_description') }}
        </p>
      </div>
      <div class="dev-eval-header-right">
        <router-link
          :to="{ name: 'company.development-evaluation.import' }"
          class="btn btn-outline-primary import-excel-link"
        >
          <i class="uil-upload-alt"></i>
          {{ $t('general.add_with_excell') }}
        </router-link>
        <excel-download-button :loading="isExporting"
                               :tooltipText="$t('filters.export_excell')"
                               @download="exportUsers"
        />
        <span class="total-users-badge">
          <i class="uil-users-alt"></i>
          {{ $t('general.total_users_count', { count: total }) }}
        </span>
      </div>
    </div>

    <div class="card evaluation-card">
      <div class="card-header border-0 evaluation-toolbar">
        <div class="toolbar-grid">
          <div class="filter-field search-field">
            <label for="development-user-search">{{ $t('general.user') }}</label>
            <div class="development-user-search-control">
              <i class="uil-search"></i>
              <input id="development-user-search"
                     v-model="search"
                     :aria-label="$t('general.search_user')"
                     :placeholder="$t('general.search_user_placeholder')"
                     class="form-control"
                     type="search"
                     @input="scheduleSearch"
                     @keydown.enter.prevent="applyFilters"
              />
            </div>
          </div>

          <div class="filter-field">
            <label>{{ $t('general.manager') }}</label>
            <multiple-select-input v-model="selectedManager"
                                   :allowEmpty="false"
                                   :disabled="loading"
                                   :searchable="true"
                                   :showLabels="false"
                                   :values="managers"
                                   class="evaluation-select"
                                   @select="selectFilter('selectedManager', $event)"
            />
          </div>

          <div class="filter-field">
            <label>{{ $t('general.department') }}</label>
            <multiple-select-input v-model="selectedDepartment"
                                   :disabled="loading"
                                   :searchable="true"
                                   :showLabels="false"
                                   :values="departments"
                                   class="evaluation-select"
                                   @select="selectFilter('selectedDepartment', $event)"
            />
          </div>

          <div class="filter-field">
            <label>{{ $t('general.competency') }}</label>
            <multiple-select-input v-model="selectedCompetency"
                                   :disabled="loading"
                                   :searchable="true"
                                   :showLabels="false"
                                   :values="competencies"
                                   class="evaluation-select"
                                   @select="selectFilter('selectedCompetency', $event)"
            />
          </div>

          <div class="filter-field date-field">
            <label for="development-start-date">{{ $t('general.period_start_date') }}</label>
            <input id="development-start-date"
                   v-model="startDate"
                   :max="endDate || undefined"
                   class="form-control"
                   type="date"
                   @change="applyFilters"
            />
          </div>

          <div class="filter-field date-field">
            <label for="development-end-date">{{ $t('general.period_end_date') }}</label>
            <input id="development-end-date"
                   v-model="endDate"
                   :min="startDate || undefined"
                   class="form-control"
                   type="date"
                   @change="applyFilters"
            />
          </div>

          <div class="filter-field sort-field">
            <label>{{ $t('general.sorting') }}</label>
            <multiple-select-input v-model="selectedSort"
                                   :allowEmpty="false"
                                   :searchable="false"
                                   :showLabels="false"
                                   :values="sortOptions"
                                   class="evaluation-select"
                                   @select="selectFilter('selectedSort', $event)"
            />
          </div>

        </div>
      </div>

      <div v-if="errorMessage" class="uk-alert-danger m-3" role="alert" uk-alert>
        <p class="mb-0">{{ errorMessage }}</p>
      </div>

      <default-loading v-if="loading && !users.length"></default-loading>

      <template v-else>
        <div v-if="users.length"
             class="sf-table sf-table--hover sf-table--clickable dev-eval-table-wrap">
          <table>
            <thead>
            <tr>
              <th>{{ $t('general.user') }}</th>
              <th>{{ $t('general.email') }}</th>
              <th>{{ $t('general.manager') }}</th>
              <th class="sf-table__center">{{ $t('general.total_competencies') }}</th>
              <th class="sf-table__col-actions" :aria-label="$t('general.detail')"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="user in users"
                :key="user.id"
                class="user-row"
                role="link"
                tabindex="0"
                @click="openUser(user)"
                @keydown.enter.prevent="openUser(user)"
                @keydown.space.prevent="openUser(user)"
            >
              <td>
                <div class="sf-table__media user-cell">
                  <AvatarOrIcon :alt="user.full_name || $t('general.user')"
                                :size-px="42"
                                :src="user.avatar || ''"
                  />
                  <div class="sf-table__media-body">
                    <strong>{{ user.full_name || "—" }}</strong>
                    <small v-if="user.title_name" class="sf-table__meta">
                      {{ user.title_name }}
                    </small>
                  </div>
                </div>
              </td>
              <td>{{ user.email || "—" }}</td>
              <td>{{ managerName(user) }}</td>
              <td class="sf-table__center">
                <span class="competency-count">
                  {{ Number(user.competencies_count) || 0 }}
                </span>
              </td>
              <td class="sf-table__col-actions">
                  <span class="detail-link">
                    {{ $t('general.detail') }} <i class="uil-angle-right-b"></i>
                  </span>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="empty-state">
          <i class="uil-users-alt"></i>
          <h4>{{ $t('general.users_not_found') }}</h4>
          <p>{{ $t('general.users_not_found_hint') }}</p>
        </div>

        <pagination v-if="totalPages > 1"
                    :pagination="pagination"
                    class="mt-2 mb-3"
                    @changePage="changePage($event)"/>
      </template>
    </div>
  </main>
</template>

<script>
import ExcelDownloadButton from "@/view/components/buttons/ExcelDownloadButton.vue";
import DefaultLoading from "@/view/components/loading/DefaultLoading.vue";
import AvatarOrIcon from "@/view/components/icons/AvatarOrIcon.vue";
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput.vue";
import Pagination from "@/assets/components/Pagination.vue";
import CompanyBreadcrumb from "@/view/company/components/breadcrumb/CompanyBreadcrumb.vue";
import ApiService from "@/core/services/api.service";
import querystring from "querystring";

export default {
  name: "DevelopmentEvaluationIndex",
  components: {
    AvatarOrIcon,
    CompanyBreadcrumb,
    DefaultLoading,
    ExcelDownloadButton,
    MultipleSelectInput,
    Pagination,
  },
  data() {
    return {
      users: [],
      managers: [],
      departments: [],
      competencies: [],
      selectedManager: null,
      selectedDepartment: null,
      selectedCompetency: null,
      selectedSort: null,
      search: "",
      startDate: "",
      endDate: "",
      page: 1,
      perPage: 15,
      total: 0,
      totalPages: 1,
      loading: false,
      isExporting: false,
      errorMessage: "",
      searchTimer: null,
      requestSequence: 0,
    };
  },
  computed: {
    breadcrumbItems() {
      return [
        { name: this.$t("general.development_evaluation") },
      ];
    },
    allManagersOption() {
      return { id: null, name: this.$t("general.all") };
    },
    sortOptions() {
      return [
        { id: "desc", name: this.$t("general.sort_highest_competency") },
        { id: "asc", name: this.$t("general.sort_lowest_competency") },
      ];
    },
    pagination() {
      return {
        current_page: this.page,
        last_page: this.totalPages,
        per_page: this.perPage,
        total: this.total,
      };
    },
  },
  created() {
    this.selectedManager = this.allManagersOption;
    this.selectedSort = this.sortOptions[0];
    this.managers = [this.allManagersOption];
    this.loadUsers();
    this.loadDepartments();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    this.requestSequence += 1;
  },
  methods: {
    queryParams(page, perPage) {
      const params = {
        page,
        perPage,
        order_by: "competencies_count",
        sort: this.selectedSort?.id || "desc",
        search: this.search.trim(),
        manager_id: this.selectedManager?.id,
        department_id: this.selectedDepartment?.id,
        competency_id: this.selectedCompetency?.id,
        // API tarihleri Y.m.d biçiminde bekliyor (input değeri YYYY-MM-DD)
        start_date: this.startDate.replace(/-/g, "."),
        end_date: this.endDate.replace(/-/g, "."),
      };
      // Boş filtreleri query string'e ekleme
      Object.keys(params).forEach((key) => {
        if (params[key] == null || params[key] === "") delete params[key];
      });
      return params;
    },
    async fetchPage(page, perPage) {
      const response = await ApiService.get(
          `api/competencies-evaluation?${querystring.stringify(
              this.queryParams(page, perPage)
          )}`
      );
      return response?.data || {};
    },
    async loadUsers() {
      const requestId = ++this.requestSequence;
      this.loading = true;
      this.errorMessage = "";

      try {
        const payload = await this.fetchPage(this.page, this.perPage);
        if (requestId !== this.requestSequence) return;

        const meta = payload.meta || {};
        this.users = Array.isArray(payload.data) ? payload.data : [];
        this.total = Number(meta.total) || this.users.length;
        this.page = Number(meta.current_page) || this.page;
        this.totalPages = Math.max(1, Number(meta.last_page) || 1);

        // Yönetici ve yetkinlik seçenekleri response içinde döner
        const filters = payload.filters || {};
        if (Array.isArray(filters.managers)) {
          this.managers = [
            this.allManagersOption,
            ...filters.managers.map((manager) => ({
              id: manager.id,
              name: manager.full_name,
            })),
          ];
        }
        if (Array.isArray(filters.competencies)) {
          this.competencies = filters.competencies;
        }
      } catch (error) {
        if (requestId !== this.requestSequence) return;
        this.users = [];
        this.total = 0;
        this.totalPages = 1;
        this.errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            this.$t("general.users_load_failed");
      } finally {
        if (requestId === this.requestSequence) this.loading = false;
      }
    },
    async loadDepartments() {
      try {
        const response = await ApiService.get("api/department?perPage=100");
        const rows = response?.data?.data || response?.data || [];
        this.departments = Array.isArray(rows)
            ? rows.filter((item) => item && item.id != null)
            : [];
      } catch (error) {
        // Departman filtresi opsiyonel; liste yüklemesini engelleme.
      }
    },
    async exportUsers() {
      if (this.isExporting) return;
      this.isExporting = true;
      this.errorMessage = "";
      let downloadUrl = null;
      let fileLink = null;

      try {
        const params = this.queryParams();
        delete params.page;
        delete params.perPage;
        const response = await ApiService.get(
          `api/competencies-evaluation/export?${querystring.stringify(params)}`,
          "",
          { responseType: "blob" }
        );
        downloadUrl = window.URL.createObjectURL(new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }));
        fileLink = document.createElement("a");
        const date = new Date();
        const timestamp = [
          date.getFullYear(),
          String(date.getMonth() + 1).padStart(2, "0"),
          String(date.getDate()).padStart(2, "0"),
          String(date.getHours()).padStart(2, "0"),
          String(date.getMinutes()).padStart(2, "0"),
          String(date.getSeconds()).padStart(2, "0"),
        ].join("");

        fileLink.href = downloadUrl;
        fileLink.setAttribute("download", `development-evaluation-${timestamp}.xlsx`);
        document.body.appendChild(fileLink);
        fileLink.click();
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || this.$t("general.try_later");
      } finally {
        fileLink?.remove();
        if (downloadUrl) window.URL.revokeObjectURL(downloadUrl);
        this.isExporting = false;
      }
    },
    // "select" eventi v-model güncellemesinden önce tetiklendiği için
    // seçimi burada set edip öyle filtreliyoruz.
    selectFilter(field, option) {
      this[field] = option;
      this.applyFilters();
    },
    scheduleSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.applyFilters, 350);
    },
    applyFilters() {
      clearTimeout(this.searchTimer);
      this.page = 1;
      this.loadUsers();
    },
    changePage(page) {
      page = Number(page);
      if (!page || page < 1 || page > this.totalPages || page === this.page) return;
      this.page = page;
      this.loadUsers();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    openUser(user) {
      if (!user?.id) return;
      this.$router.push({
        name: "company.development-evaluation.user",
        params: { userId: String(user.id) },
      });
    },
    managerName(user) {
      return user?.parent?.full_name || "—";
    },
  },
};
</script>

<style scoped>
.development-evaluation-page {
  --dev-eval-primary: var(--primaryColor, #3e416d);
  --dev-eval-text: #1e293b;
  --dev-eval-muted: #64748b;
  --dev-eval-border: #e2e8f0;
  width: 100%;
  min-width: 0;
}

/* ── Header ── */
.dev-eval-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.dev-eval-title {
  margin: 0;
  color: var(--dev-eval-text);
  font-size: 19px;
  font-weight: 600;
  line-height: 1.3;
}

.dev-eval-description {
  margin: 4px 0 0;
  color: var(--dev-eval-muted);
  font-size: 14px;
  line-height: 1.4;
}

.dev-eval-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.import-excel-link {
  align-items: center;
  display: inline-flex;
  gap: 6px;
  white-space: nowrap;
}

.total-users-badge {
  align-items: center;
  background: #eef4ff;
  border: 1px solid #dbe8ff;
  border-radius: 8px;
  color: #315ca8;
  display: inline-flex;
  font-size: 14px;
  font-weight: 600;
  gap: 7px;
  padding: 9px 13px;
  white-space: nowrap;
}

/* ── Card ── */
.evaluation-card {
  border: 1px solid var(--dev-eval-border);
  border-radius: 14px;
  overflow: visible;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

/* ── Toolbar / Filters ── */
.evaluation-toolbar {
  background: #fff;
  border-radius: 14px 14px 0 0;
  padding: 20px;
}

.toolbar-grid {
  align-items: end;
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.filter-field {
  min-width: 0;
}

.filter-field label {
  color: #4a5568;
  display: block;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 7px;
  text-transform: uppercase;
}

.development-user-search-control {
  display: block;
  height: 42px;
  width: 100%;
  position: relative;
}

.development-user-search-control i {
  color: #94a0af;
  left: 13px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
}

.development-evaluation-page .evaluation-toolbar .development-user-search-control > input[type="search"] {
  box-sizing: border-box;
  display: block;
  opacity: 1;
  visibility: visible;
  width: 100%;
  height: 42px;
  min-height: 42px;
  padding: 0 12px 0 38px;
  margin: 0;
  border: 1px solid var(--dev-eval-border);
  border-radius: 10px;
  outline: 0;
  background: #fff;
  box-shadow: none;
  font-size: 13px !important;
  font-weight: 400;
  line-height: 1.2;
  appearance: none;
  -webkit-appearance: none;
}

.development-evaluation-page .evaluation-toolbar .development-user-search-control > input[type="search"]:focus {
  border-color: var(--dev-eval-primary);
  outline: 0;
  box-shadow: 0 0 0 3px rgba(62, 65, 109, 0.12);
}

/* ── Select dropdowns ── */
.evaluation-select {
  width: 100%;
  margin: 0;
}

.evaluation-select :deep(.form-group) {
  margin: 0;
}

.evaluation-select :deep(.multiselect) {
  height: auto !important;
  min-height: 42px !important;
  max-height: none !important;
  overflow: visible !important;
}

.evaluation-select :deep(.multiselect--active) {
  position: relative;
  z-index: 30;
}

.evaluation-select :deep(.multiselect__content-wrapper) {
  z-index: 40;
  border: 1px solid var(--dev-eval-border);
  border-radius: 10px;
  box-shadow: 0 10px 24px rgba(30, 41, 59, 0.12);
}

.evaluation-select :deep(.multiselect__tags) {
  box-sizing: border-box;
  height: 42px !important;
  min-height: 42px !important;
  max-height: 42px !important;
  padding: 8px 40px 0 12px;
  border: 1px solid var(--dev-eval-border);
  border-radius: 10px;
  background: #fff;
}

.evaluation-select :deep(.multiselect__select) {
  height: 40px;
}

.evaluation-select :deep(.multiselect__placeholder),
.evaluation-select :deep(.multiselect__single) {
  box-sizing: border-box !important;
  width: 100% !important;
  height: 24px !important;
  min-height: 24px !important;
  margin: 0 0 8px !important;
  padding: 0 !important;
  color: #4a5568 !important;
  font-size: 13px !important;
  font-weight: 400 !important;
  line-height: 24px !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}

.evaluation-select :deep(.multiselect:not(.multiselect--active) .multiselect__input) {
  position: absolute !important;
  width: 0 !important;
  min-width: 0 !important;
  height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  overflow: hidden !important;
}

.evaluation-select :deep(.multiselect--active .multiselect__input) {
  position: relative !important;
  width: 100% !important;
  height: 24px !important;
  min-height: 24px !important;
  margin: 0 0 8px !important;
  padding: 0 !important;
  border: 0 !important;
  outline: 0 !important;
  box-shadow: none !important;
  font-size: 13px !important;
  line-height: 24px !important;
}

.evaluation-select :deep(.multiselect--active .multiselect__tags) {
  border-color: var(--dev-eval-primary);
  box-shadow: 0 0 0 3px rgba(62, 65, 109, 0.12);
}

.evaluation-select :deep(.multiselect--disabled) {
  opacity: 0.7;
}

/* ── Date inputs ── */
.development-evaluation-page .evaluation-toolbar .date-field > input[type="date"] {
  box-sizing: border-box;
  display: block;
  width: 100%;
  height: 42px;
  min-height: 42px;
  padding: 0 12px;
  margin: 0;
  color: #4a5568;
  background: #fff;
  border: 1px solid var(--dev-eval-border);
  border-radius: 10px;
  box-shadow: none;
  font-size: 13px !important;
  font-weight: 400;
  line-height: 1.2;
}

.development-evaluation-page .evaluation-toolbar .date-field > input[type="date"]:focus {
  border-color: var(--dev-eval-primary);
  outline: 0;
  box-shadow: 0 0 0 3px rgba(62, 65, 109, 0.12);
}

/* ── Table (Desktop) ──
   Tasarim .sf-table'dan geliyor (assets/scss/_data-table.scss);
   burada yalnizca bu sayfanin olcu/renk degerleri ayarlaniyor. */
.dev-eval-table-wrap {
  --sf-table-cell-y: 15px;
  --sf-table-cell-x: 18px;
  --sf-table-row-border: #edf0f4;
  --sf-table-head-bg: #f8fafc;
  --sf-table-head-text: #718096;
  --sf-table-hover: #f8fbff;
}

/* hover sistemden geliyor; focus klavye erisimi icin burada kaliyor */
.user-row:focus {
  background: #f8fbff;
  outline: none;
}

.user-cell {
  align-items: center;
  display: flex;
  gap: 12px;
  min-width: 210px;
}

.competency-count {
  background: #ecf9f1;
  border-radius: 999px;
  color: #208653;
  display: inline-flex;
  font-weight: 700;
  justify-content: center;
  min-width: 38px;
  padding: 5px 11px;
  flex-shrink: 0;
}

.detail-link {
  color: #3975d1;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

/* ── Empty state ── */
.empty-state {
  color: #778295;
  padding: 56px 20px;
  text-align: center;
}

.empty-state i {
  color: #a9b6c7;
  font-size: 48px;
}

.empty-state h4 {
  color: #3d4756;
  margin: 12px 0 5px;
}

/* ════════════════════════════════════════════
   Tablet — max 1200px
   ════════════════════════════════════════════ */
@media (max-width: 1200px) {
  .toolbar-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* ════════════════════════════════════════════
   Tablet / Small — max 959px
   ════════════════════════════════════════════ */
@media (max-width: 959px) {
  .dev-eval-header {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .dev-eval-title {
    font-size: 20px;
    font-weight: 700;
  }

  .dev-eval-description {
    font-size: 15px;
  }

  .dev-eval-header-right {
    justify-content: flex-end;
  }

  .toolbar-grid {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .search-field {
    grid-column: 1 / -1;
  }

  /* Bigger touch targets */
  .development-evaluation-page .evaluation-toolbar .development-user-search-control > input[type="search"] {
    height: 48px;
    min-height: 48px;
    padding: 12px 12px 12px 42px;
    font-size: 16px !important;
    border-radius: 10px;
  }

  .development-user-search-control {
    height: 48px;
  }

  .evaluation-select :deep(.multiselect) {
    min-height: 48px !important;
  }

  .evaluation-select :deep(.multiselect__tags) {
    height: 48px !important;
    min-height: 48px !important;
    max-height: 48px !important;
    padding: 11px 40px 0 12px;
    border-radius: 10px;
  }

  .evaluation-select :deep(.multiselect__select) {
    height: 46px;
  }

  .evaluation-select :deep(.multiselect__placeholder),
  .evaluation-select :deep(.multiselect__single) {
    font-size: 15px !important;
  }

  .development-evaluation-page .evaluation-toolbar .date-field > input[type="date"] {
    height: 48px;
    min-height: 48px;
    font-size: 16px !important;
    padding: 12px;
    border-radius: 10px;
  }

  .filter-field label {
    font-size: 13px;
  }

  .dev-eval-table-wrap {
    --sf-table-min-width: 700px;
  }
}

/* ════════════════════════════════════════════
   Mobile — max 767px
   ════════════════════════════════════════════ */
@media (max-width: 767px) {
  .development-evaluation-page {
    padding: 0 0 24px;
  }

  .dev-eval-header {
    gap: 8px;
    margin-bottom: 12px;
  }

  .dev-eval-title {
    font-size: 18px;
  }

  .dev-eval-description {
    font-size: 14px;
  }

  .total-users-badge {
    font-size: 13px;
    padding: 8px 12px;
  }

  .evaluation-card {
    border-radius: 14px;
  }

  .evaluation-toolbar {
    padding: 14px;
    border-radius: 14px 14px 0 0;
  }

  .toolbar-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .search-field {
    grid-column: auto;
  }

  .dev-eval-table-wrap {
    --sf-table-min-width: 700px;
  }
}

/* ════════════════════════════════════════════
   Hover & reduced motion
   ════════════════════════════════════════════ */
/* hover / dokunmatik hover iptali / reduced-motion: ucu de .sf-table
   icinde tanimli (--sf-table-hover degeri yukarida ayarlandi). */
</style>
